import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import {
  createErrorContext,
  createIsraeliTimestamp,
  logGraphQLErrors,
  logNetworkError,
} from "./errorLink"
import { getGraphQLUrl } from "./graphqlUrl"
import generatedIntrospection from "./introspection-result.json"

const GRAPHQL_URL = getGraphQLUrl()

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => !!error,
  },
})

const errorLink = onError(({ graphQLErrors, operation, networkError }) => {
  try {
    const errorContext = createErrorContext(operation, GRAPHQL_URL)

    if (graphQLErrors) {
      logGraphQLErrors(graphQLErrors, errorContext)
    }

    if (networkError) {
      logNetworkError(networkError, errorContext)
    }
  } catch (errorHandlingError) {
    // Fallback error logging if our error handling fails
    console.error("=== ERROR HANDLER FAILED ===")
    console.error("Error handling error:", errorHandlingError)
    console.error("Original GraphQL errors:", graphQLErrors)
    console.error("Original network error:", networkError)
    console.error("Operation:", operation)
    console.error("GraphQL URL:", GRAPHQL_URL)
    console.error("Timestamp:", createIsraeliTimestamp())
    console.error("==============================")
  }
})

// CRITICAL FIX: Create a singleton client instance for middleware
// Previously, a new client was created on every middleware request, causing severe memory leaks
let middlewareClientInstance: ApolloClient<any> | null = null

// Use only @apollo/client imports (not integration packages) for middleware compatibility
export const createMiddlewareClient = () => {
  return new ApolloClient({
    cache: new InMemoryCache({
      possibleTypes: generatedIntrospection.possibleTypes,
    }),
    link: ApolloLink.from([
      retryLink,
      errorLink,
      new HttpLink({
        uri: GRAPHQL_URL,
      }),
    ]),
    defaultOptions: {
      query: {
        errorPolicy: "all",
        fetchPolicy: "network-only", // Always fresh data for middleware
      },
    },
  })
}

export const getClientForMiddleware = () => {
  // Return existing instance or create new one (singleton pattern)
  if (!middlewareClientInstance) {
    middlewareClientInstance = createMiddlewareClient()
  }
  return middlewareClientInstance
}
